<template>
  <div class="ma-4 px-4">
    <v-sheet outlined>
      <v-data-table
        :headers="programSchemeTermHeaders"
        :items="[program_scheme_term]"
        hide-default-footer
        fixed-header
        class="secondary row-height-50"
      >
        <template #[`item.action`]="{ item }">
          <v-icon small class="mr-2" color="accent" @click="editTerm(item)">
            mdi-pencil
          </v-icon>
        </template>
        <template #[`item.is_stream`]="{ item }">
          <span>{{ item.is_stream ? 'Yes' : 'No' }}</span>
        </template>
      </v-data-table>
    </v-sheet>

    <div class="pt-6">
      <span class="text-5xl font-weight-bold accent--text">COURSES</span>
    </div>

    <searchBar
      @add="(dialog = true), (editMode = false)"
      @updateSearch="(v) => (search = v)"
      headerText=""
      :disableAdd="addDisabled"
      class="mt-2"
      @saveOrder="saveOrder"
      :showOrderBtn="dataModified"
    />

    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :search="search"
            :items="mainTableCourses"
            fixed-header
            :loading="loading"
            class="secondary row-height-50"
          >
            <template #[`item.order_controls`]="{ index }">
              <span v-if="mainTableCourses.length > 1">
                <v-icon
                  small
                  @click="moveUp(index)"
                  :disabled="index === 0"
                  class="mr-1"
                  color="primary"
                >
                  mdi-arrow-up
                </v-icon>
                <v-icon
                  small
                  @click="moveDown(index)"
                  :disabled="index === mainTableCourses.length - 1"
                  color="primary"
                >
                  mdi-arrow-down
                </v-icon>
              </span>
            </template>

            <template #[`item.course_name`]="{ item }">
              <div>
                <span
                  v-if="item.type === 'Elective'"
                  class="text-caption ml-n1"
                >
                  <v-chip
                    small
                    @click="showData('Elective Courses', getCourseNames(item))"
                  >
                    {{ formatElectiveNames(getCourseNames(item)) }}
                  </v-chip>
                </span>
                <span v-else class="text-caption">
                  {{ getCoreCourseField(item, "name") }}
                </span>
              </div>
            </template>

            <template #[`item.course_code`]="{ item }">
              <div>
                <span
                  v-if="item.type === 'Elective'"
                  class="text-caption ml-n1"
                >
                  <v-chip
                    small
                    @click="
                      showData('Elective Course Codes', getCourseCodes(item))
                    "
                  >
                    {{ formatElectiveNames(getCourseCodes(item)) }}
                  </v-chip>
                </span>
                <span v-else class="text-caption">
                  {{ getCoreCourseField(item, "code") }}
                </span>
              </div>
            </template>

            <template #[`item.assessment_scheme_name`]="{ item }">
              <span v-if="item.type === 'Elective'" class="text-caption">
                <v-chip
                  small
                  @click="
                    showData(
                      'Assessment Schemes',
                      getElectiveAssessmentNames(item),
                    )
                  "
                >
                  {{ formatElectiveNames(getElectiveAssessmentNames(item)) }}
                </v-chip>
              </span>
              <span v-else class="text-caption">
                {{ getCoreCourseField(item, "assessment_name") || "-" }}
              </span>
            </template>

            <template #[`item.action`]="{ item }">
              <v-icon small class="mr-2" color="accent" @click="edit(item)">
                mdi-pencil
              </v-icon>
              <v-icon
                small
                class="mr-2"
                color="accent"
                :disabled="program_scheme.status == 'Published'"
                @click="deleteOpenDialog(item)"
              >
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <template v-if="program_scheme_term.is_stream">
      <div class="pt-6">
        <span class="text-5xl font-weight-bold accent--text">STREAMS</span>
      </div>

      <v-row class="mt-2">
        <v-col cols="12">
          <v-sheet outlined>
            <v-data-table
              :headers="streamHeaders"
              :items="streamRows"
              hide-default-footer
              fixed-header
              :loading="loading"
              class="secondary row-height-50"
              show-expand
              :expanded.sync="expandedStreams"
              item-key="stream_name"
              :single-expand="false"
            >
              <template #[`item.stream_name`]="{ item }">
                <span class="text-caption font-weight-medium">{{
                  item.stream_name
                }}</span>
              </template>

              <template #[`item.course_name`]="{ item }">
                <span v-if="item.record" class="text-caption ml-n1">
                  <v-chip
                    small
                    @click="
                      showData(
                        `${item.stream_name} Courses`,
                        getCourseNames(item.record),
                      )
                    "
                  >
                    {{ formatElectiveNames(getCourseNames(item.record)) }}
                  </v-chip>
                </span>
                <span v-else class="text-caption grey--text">
                  Not configured
                </span>
              </template>

              <template #[`item.total_credits`]="{ item }">
                <span v-if="item.record">
                  <v-chip
                    small
                    :color="
                      streamCreditsMatch(item.record) ? 'success' : 'error'
                    "
                    text-color="white"
                  >
                    {{ streamCourseCredits(item.record) }}
                  </v-chip>
                  <div
                    v-if="!streamCreditsMatch(item.record)"
                    class="text-caption error--text mt-1"
                  >
                    Expected {{ program_scheme_term.stream_credits }}
                  </div>
                </span>
                <span v-else class="text-caption grey--text">—</span>
              </template>

              <template #[`item.assessment_scheme_name`]="{ item }">
                <span v-if="item.record" class="text-caption">
                  <v-chip
                    small
                    @click="
                      showData(
                        'Assessment Schemes',
                        getElectiveAssessmentNames(item.record),
                      )
                    "
                  >
                    {{
                      formatElectiveNames(
                        getElectiveAssessmentNames(item.record),
                      )
                    }}
                  </v-chip>
                </span>
                <span v-else class="text-caption grey--text">—</span>
              </template>

              <template #[`item.action`]="{ item }">
                <v-icon
                  v-if="!item.record"
                  small
                  class="mr-2"
                  color="accent"
                  @click="addStream(item.stream_name)"
                >
                  mdi-plus
                </v-icon>
                <template v-else>
                  <v-icon
                    small
                    class="mr-2"
                    color="accent"
                    @click="edit(item.record)"
                  >
                    mdi-pencil
                  </v-icon>
                  <v-icon
                    small
                    class="mr-2"
                    color="accent"
                    :disabled="program_scheme.status == 'Published'"
                    @click="deleteOpenDialog(item.record)"
                  >
                    mdi-delete
                  </v-icon>
                </template>
              </template>

              <template #expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-3">
                  <div v-if="!item.record" class="text-caption grey--text">
                    No courses configured for this stream yet.
                  </div>
                  <div v-else>
                    <div class="d-flex align-center justify-space-between mb-2">
                      <span
                        class="text-caption font-weight-medium accent--text"
                      >
                        Course order - {{ item.stream_name }}
                      </span>
                      <v-btn
                        v-if="streamOrderModified[item.record.id]"
                        x-small
                        color="primary"
                        @click="saveStreamOrder(item.record)"
                      >
                        Save Order
                      </v-btn>
                    </div>
                    <v-simple-table dense>
                      <thead>
                        <tr>
                          <th class="text-caption text-center"></th>
                          <th class="text-caption">Order</th>
                          <th class="text-caption">Course Name</th>
                          <th class="text-caption">Course Code</th>
                          <th class="text-caption">Credits</th>
                          <th class="text-caption">Assessment</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(course, idx) in sortedStreamCourses(
                            item.record,
                          )"
                          :key="course.course_id"
                        > 
                        <td class="text-center">
                            <v-icon
                              small
                              @click="moveStreamCourseUp(item.record, idx)"
                              :disabled="idx === 0"
                              class="mr-1"
                              color="primary"
                            >
                              mdi-arrow-up
                            </v-icon>
                            <v-icon
                              small
                              @click="moveStreamCourseDown(item.record, idx)"
                              :disabled="
                                idx ===
                                sortedStreamCourses(item.record).length - 1
                              "
                              color="primary"
                            >
                              mdi-arrow-down
                            </v-icon>
                          </td>
                          <td class="text-caption">
                            {{ course.order_sequence }}
                          </td>
                          <td class="text-caption">
                            {{
                              courseMap[course.course_id]?.name ||
                              `#${course.course_id}`
                            }}
                          </td>
                          <td class="text-caption">
                            {{
                              courseMap[course.course_id]?.code ||
                              `#${course.course_id}`
                            }}
                          </td>
                          <td class="text-caption">{{ course.credits }}</td>
                          <td class="text-caption">
                            {{
                              assessmentMap[course.assessment_scheme_id]
                                ?.name || "—"
                            }}
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </div>
                </td>
              </template>
            </v-data-table>
          </v-sheet>
        </v-col>
      </v-row>
    </template>

    <v-dialog v-model="showDataDialog" max-width="600" persistent scrollable>
      <v-card class="secondary">
        <v-card-title class="primary white--text">
          {{ showTitle }}
          <v-spacer></v-spacer>
          <v-btn small icon @click="showDataDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="mt-3 reset-card-text">
          <ul>
            <li v-for="(item, index) in electiveData" :key="index" class="mb-1">
              {{ item }}
            </li>
          </ul>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" width="950" persistent scrollable>
      <formDialog
        v-if="dialog"
        @closeDialog="dialog = false"
        @create="createPSTCourse"
        @update="updatePSTCourse"
        :pst_course="pst_course"
        :program_scheme_term="program_scheme_term"
        :program_scheme="program_scheme"
        :upcomingOrder="upcomingOrder"
        :edit="editMode"
        :pst_courses="pst_courses"
        :lockedStreamName="lockedStreamName"
      />
    </v-dialog>

    <v-dialog v-model="termDialog" width="800" persistent scrollable>
      <termFormDialog
        v-if="termDialog"
        @closeDialog="termDialog = false"
        @update="updateProgramSchemeTerm"
        :program_scheme_term="program_scheme_term"
        :program_schemes="[program_scheme]"
        :edit="editTermMode"
      />
    </v-dialog>

    <v-dialog v-model="deleteDialog" width="500" persistent>
      <confirmationDialog
        v-if="deleteDialog"
        confirmationText="Are you sure you want to delete? This action cannot be undone."
        @closeDialog="deleteDialog = false"
        @saveDialog="deleteItem()"
      />
    </v-dialog>
  </div>
</template>

<script>
import termFormDialog from "@/components/scheme/program_scheme_term.vue";
import formDialog from "@/components/scheme/pst_course.vue";
import confirmationDialog from "@/components/confirmationDialog.vue";
import searchBar from "@/components/searchBarFilter.vue";
import {
  programSchemeTermCoursesHeaders,
  programSchemeTermHeaders,
} from "@/constants/headers";

export default {
  components: {
    termFormDialog,
    formDialog,
    searchBar,
    confirmationDialog,
  },

  data() {
    return {
      programSchemeTermHeaders: programSchemeTermHeaders,
      headers: programSchemeTermCoursesHeaders,
      streamHeaders: [
        { text: "Stream", value: "stream_name", sortable: false },
        { text: "Courses", value: "course_name", sortable: false },
        {
          text: "Total Credits",
          value: "total_credits",
          sortable: false,
          align: "center",
        },
        {
          text: "Assessment Scheme",
          value: "assessment_scheme_name",
          sortable: false,
        },
        { text: "Action", value: "action", sortable: false, align: "center" },
      ],
      dialog: false,
      editMode: false,
      editTermMode: false,
      termDialog: false,
      search: "",
      pst_course: {},
      program_scheme: {},
      pst_courses: [],
      program_scheme_term: {},
      partners: [],
      dataModified: false,
      upcomingOrder: null,
      showDataDialog: false,
      electiveData: [],
      loading: false,
      deleteDialog: false,
      showTitle: "",
      lockedStreamName: null,
      expandedStreams: [],
      streamOrderModified: {},

      courseMap: {},
      assessmentMap: {},
    };
  },

  computed: {
    mainTableCourses() {
      if (!this.program_scheme_term.is_stream) return this.pst_courses;
      return this.pst_courses.filter((c) => c.type !== "Elective");
    },

    streamElectiveCourses() {
      return this.pst_courses.filter((c) => c.type === "Elective");
    },

    streamRows() {
      const byStream = {};
      this.streamElectiveCourses.forEach((c) => {
        const sn = this.getStreamName(c);
        if (sn) byStream[sn] = c;
      });
      return (this.program_scheme_term.stream_names || []).map((sn) => ({
        stream_name: sn,
        record: byStream[sn] || null,
      }));
    },

    addDisabled() {
      const coreCount = this.pst_courses.filter(
        (c) => c.type === "Core",
      ).length;
      const coreFull =
        coreCount >= (this.program_scheme_term?.no_of_courses || 0);

      if (this.program_scheme_term.is_stream) {
        return coreFull;
      }

      const electiveCount = this.pst_courses.filter(
        (c) => c.type === "Elective",
      ).length;
      const electiveFull =
        electiveCount >= (this.program_scheme_term?.no_of_electives || 0);
      return coreFull && electiveFull;
    },
  },

  async created() {
    this.pst_id = this.$route.query.id;
    this.pst_name = this.$route.query.name;
    this.programSchemeTermHeaders = this.programSchemeTermHeaders.filter(
      (h) => h.value !== "courses",
    );
    await this.initialise();
    this.$store.commit("setBreadcrumbs", [
      { text: "Programme Schemes", to: { name: "Programme Schemes" } },
      {
        text: this.pst_name,
        to: {
          path: "/view_program_schemes",
          query: { id: this.program_scheme.id, name: this.pst_name },
        },
      },
      { text: "Term Courses" },
    ]);
  },

  methods: {
    async initialise() {
      this.loading = true;
      const [
        pstCoursesRes,
        pstRes,
        psRes,
        partnersRes,
        coursesRes,
        assessmentsRes,
      ] = await Promise.all([
        this.$axios.get(
          `program_scheme_term_courses?program_scheme_term_id=${this.pst_id}`,
        ),
        this.$axios.get("program_scheme_terms"),
        this.$axios.get("program_schemes"),
        this.$axios.get("partners"),
        this.$axios.get("courses"),
        this.$axios.get("assessment_schemes"),
      ]);

      this.courseMap = Object.fromEntries(
        (coursesRes.data.data || []).map((c) => [c.id, c]),
      );
      this.assessmentMap = Object.fromEntries(
        (assessmentsRes.data.data || []).map((a) => [a.id, a]),
      );

      this.partners = partnersRes.data.data || [];

      this.pst_courses = (pstCoursesRes.data.data || []).sort(
        (a, b) => a.order_sequence - b.order_sequence,
      );

      this.program_scheme_term =
        (pstRes.data.data || []).find((s) => s.id == this.pst_id) || {};

      const mainCourses = this.program_scheme_term.is_stream
        ? this.pst_courses.filter((c) => c.type !== "Elective")
        : this.pst_courses;
      const maxOrder = mainCourses.length
        ? Math.max(...mainCourses.map((c) => c.order_sequence || 0))
        : 0;
      this.upcomingOrder = maxOrder + 1;

      this.program_scheme =
        (psRes.data.data || []).find(
          (s) => s.id == this.program_scheme_term.program_scheme_id,
        ) || {};

      this.loading = false;
    },

    getCourseNames(item) {
      return (item.courses || []).map(
        (e) => this.courseMap[e.course_id]?.name || `#${e.course_id}`,
      );
    },

    getStreamName(record) {
      if (!record) return null;
      return (
        record.stream_name ||
        (record.courses &&
          record.courses[0] &&
          record.courses[0].stream_name) ||
        null
      );
    },

    getCourseCodes(item) {
      return (item.courses || []).map(
        (e) => this.courseMap[e.course_id]?.code || `#${e.course_id}`,
      );
    },

    getElectiveAssessmentNames(item) {
      return (item.courses || []).map((e) => {
        const name = this.assessmentMap[e.assessment_scheme_id]?.name;
        const courseName =
          this.courseMap[e.course_id]?.name || `#${e.course_id}`;
        return name ? `${courseName}: ${name}` : `${courseName}: —`;
      });
    },

    streamCourseCredits(record) {
      return (record?.courses || []).reduce(
        (sum, c) => sum + (Number(c.credits) || 0),
        0,
      );
    },

    streamCreditsMatch(record) {
      const target = Number(this.program_scheme_term.stream_credits);
      if (!target) return true;
      return this.streamCourseCredits(record) === target;
    },

    sortedStreamCourses(record) {
      return [...(record.courses || [])].sort(
        (a, b) => a.order_sequence - b.order_sequence,
      );
    },

    moveStreamCourseUp(record, index) {
      if (index === 0) return;
      const sorted = this.sortedStreamCourses(record);
      const tmp = sorted[index].order_sequence;
      sorted[index].order_sequence = sorted[index - 1].order_sequence;
      sorted[index - 1].order_sequence = tmp;
      record.courses = sorted;
      this.$set(this.streamOrderModified, record.id, true);
    },

    moveStreamCourseDown(record, index) {
      const sorted = this.sortedStreamCourses(record);
      if (index === sorted.length - 1) return;
      const tmp = sorted[index].order_sequence;
      sorted[index].order_sequence = sorted[index + 1].order_sequence;
      sorted[index + 1].order_sequence = tmp;
      record.courses = sorted;
      this.$set(this.streamOrderModified, record.id, true);
    },

    async saveStreamOrder(record) {
      const streamName = this.getStreamName(record);
      const courses = this.sortedStreamCourses(record).map((c) => ({
        ...c,
        stream_name: c.stream_name || streamName,
      }));
      try {
        await this.$axios.put(`program_scheme_term_courses/${record.id}`, {
          program_scheme_term_course: {
            id: record.id,
            courses,
          },
        });
        this.$store.commit("showSnackbar", {
          message: "Course order updated successfully",
          color: "success",
        });
        this.$set(this.streamOrderModified, record.id, false);
        this.initialise();
      } catch (error) {
        const msg = await this.$store.dispatch("throwError", error.response);
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
      }
    },

    getCoreCourseField(item, field) {
      const entry = (item.courses || [])[0];
      if (!entry) return "—";
      if (field === "name") return this.courseMap[entry.course_id]?.name || "—";
      if (field === "code") return this.courseMap[entry.course_id]?.code || "—";
      if (field === "assessment_name")
        return this.assessmentMap[entry.assessment_scheme_id]?.name || "—";
      return "—";
    },

    formatElectiveNames(names) {
      if (!names || names.length === 0) return "—";
      if (names.length === 1) return names[0];
      return `${names[0]}  +${names.length - 1} other${
        names.length - 1 > 1 ? "s" : ""
      }`;
    },

    showData(title, data) {
      this.showTitle = title;
      this.electiveData = Array.isArray(data) ? data : [data];
      this.showDataDialog = true;
    },

    addStream(streamName) {
      this.pst_course = {};
      this.lockedStreamName = streamName;
      this.editMode = false;
      this.dialog = true;
    },

    async createPSTCourse(data) {
      await this.$axios
        .post("program_scheme_term_courses", {
          program_scheme_term_course: data,
        })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Course created successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const msg = await this.$store.dispatch("throwError", error.response);
          this.$store.commit("showSnackbar", { message: msg, color: "error" });
        });
    },

    async updatePSTCourse(data) {
      await this.$axios
        .put("program_scheme_term_courses/" + data.id, {
          program_scheme_term_course: data,
        })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Course updated successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const msg = await this.$store.dispatch("throwError", error.response);
          this.$store.commit("showSnackbar", { message: msg, color: "error" });
        });
    },

    async updateProgramSchemeTerm(data) {
      await this.$axios
        .put("program_scheme_terms/" + data.id, { program_scheme_term: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Term updated successfully",
            color: "success",
          });
          this.resetTermValues();
          this.initialise();
        })
        .catch(async (error) => {
          const msg = await this.$store.dispatch("throwError", error.response);
          this.$store.commit("showSnackbar", { message: msg, color: "error" });
        });
    },

    moveUp(index) {
      if (index === 0) return;
      const arr = [...this.mainTableCourses];
      [arr[index], arr[index - 1]] = [arr[index - 1], arr[index]];
      const tempOrder = arr[index].order_sequence;
      arr[index].order_sequence = arr[index - 1].order_sequence;
      arr[index - 1].order_sequence = tempOrder;
      this.syncMainOrderBack(arr);
      this.dataModified = true;
    },

    moveDown(index) {
      if (index === this.mainTableCourses.length - 1) return;
      const arr = [...this.mainTableCourses];
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      const tempOrder = arr[index].order_sequence;
      arr[index].order_sequence = arr[index + 1].order_sequence;
      arr[index + 1].order_sequence = tempOrder;
      this.syncMainOrderBack(arr);
      this.dataModified = true;
    },

    syncMainOrderBack(reorderedMain) {
      const byId = Object.fromEntries(reorderedMain.map((c) => [c.id, c]));
      this.pst_courses = this.pst_courses.map((c) => byId[c.id] || c);
    },

    saveOrder() {
      const payload = this.mainTableCourses.map(({ id, order_sequence }) => ({
        id,
        order_sequence,
      }));

      this.$axios
        .post("/rearrange_order", { program_scheme_term_course: payload })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Order sequence updated successfully",
            color: "success",
          });
          this.dataModified = false;
        })
        .catch(() => {
          this.$store.commit("showSnackbar", {
            message: "Failed to update order",
            color: "error",
          });
        });
    },

    deleteOpenDialog(item) {
      if (this.program_scheme.status === "Published") {
        this.$store.commit("showSnackbar", {
          message:
            "This program scheme is already published and cannot be deleted.",
          color: "error",
        });
        return;
      }
      this.pst_course = { ...item };
      this.deleteDialog = true;
    },

    async deleteItem() {
      try {
        await this.$axios.delete(
          `program_scheme_term_courses/${this.pst_course.id}`,
        );

        const remainingMain = this.mainTableCourses.filter(
          (c) => c.id !== this.pst_course.id,
        );
        const payload = remainingMain.map((c, i) => ({
          id: c.id,
          order_sequence: i + 1,
        }));

        if (payload.length) {
          await this.$axios.post("/rearrange_order", {
            program_scheme_term_course: payload,
          });
        }

        this.$store.commit("showSnackbar", {
          message: "Term deleted successfully",
          color: "success",
        });
        this.resetValues();
        this.initialise();
      } catch (error) {
        const msg = await this.$store.dispatch("throwError", error.response);
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
      }
    },

    edit(item) {
      this.pst_course = { ...item };
      this.lockedStreamName =
        item.type === "Elective" && this.program_scheme_term.is_stream
          ? this.getStreamName(item)
          : null;
      this.editMode = true;
      this.dialog = true;
    },

    editTerm(item) {
      this.program_scheme_term = { ...item };
      this.editTermMode = true;
      this.termDialog = true;
    },

    resetValues() {
      this.editMode = false;
      this.dialog = false;
      this.pst_course = {};
      this.lockedStreamName = null;
      this.deleteDialog = false;
    },

    resetTermValues() {
      this.editTermMode = false;
      this.termDialog = false;
    },
  },
};
</script>

<style></style>
